import * as THREE from 'three'

export function createFactoryQuality({ palette }) {
  const root = new THREE.Group()
  const geometries = new Set(), materials = new Set()
  const cycleSeconds = 3.2, staticTime = cycleSeconds * .55
  const geometry = value => { geometries.add(value); return value }
  function material(color, options = {}) {
    const value = new THREE.MeshBasicMaterial({ color, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1, ...options })
    materials.add(value)
    return value
  }
  const face = Object.fromEntries(Object.entries(palette).map(([key, color]) => [key, material(color)]))
  face.gray = material('#526675')
  face.green = material('#20ab68')
  face.screen = material('#103876')
  const edge = new THREE.LineBasicMaterial({ color: palette.line })
  materials.add(edge)
  const normalFaces = [face.white, face.pale, face.white, face.pale, face.pale, face.white]
  function outlined(g, fill, parent = root) {
    const group = new THREE.Group()
    group.add(new THREE.Mesh(geometry(g), fill))
    group.add(new THREE.LineSegments(geometry(new THREE.EdgesGeometry(g, 25)), edge))
    parent.add(group)
    return group
  }
  function box(w, h, d, x, y, z, fill = normalFaces, parent = root) {
    const value = outlined(new THREE.BoxGeometry(w, h, d), fill, parent)
    value.position.set(x, y, z)
    return value
  }
  function rod(radius, length, x, y, z, fill = face.pale, parent = root) {
    const value = outlined(new THREE.CylinderGeometry(radius, radius, length, 16), fill, parent)
    value.position.set(x, y, z)
    return value
  }
  const ground = new THREE.Mesh(geometry(new THREE.PlaneGeometry(4.8, 3.4)), face.ground)
  ground.rotation.x = -Math.PI / 2
  ground.position.y = -.012
  root.add(ground)
  for (const x of [-1.72, 1.72]) for (const z of [-.95, .95]) box(.10, .88, .10, x, .44, z)
  box(4.0, .13, 2.55, 0, .935, 0)
  box(.45, .04, .45, .86, 1.02, -.62)
  box(.08, .48, .08, .86, 1.27, -.62)
  box(1.50, 1.02, .10, .86, 1.77, -.62)
  box(1.27, .79, .018, .86, 1.77, -.556, face.screen)

  // Compact test fixture: PCB nest, stationary guide rods and a descending probe plate.
  const machineX = -.88, machineZ = .26
  box(1.12, .13, .94, machineX, 1.075, machineZ)
  box(.72, .045, .59, machineX, 1.1625, machineZ, face.pale)
  box(.49, .018, .39, machineX, 1.194, machineZ, face.blue)
  box(.25, .042, .23, machineX, 1.224, machineZ, face.gray)
  for (const x of [-.18, .18]) for (const z of [-.115, .115]) {
    box(.025, .008, .028, machineX + x, 1.208, machineZ + z, face.white)
  }
  for (const x of [-.39, .39]) {
    box(.16, .12, .22, machineX + x, 1.18, machineZ - .12, face.pale)
    rod(.035, .87, machineX + x, 1.665, machineZ - .12)
    rod(.060, .10, machineX + x, 1.32, machineZ - .12, face.white)
  }
  box(1.00, .14, .39, machineX, 2.17, machineZ - .12, [face.side, face.pale, face.pale, face.pale, face.white, face.white])
  box(.22, .27, .17, machineX, 2.04, machineZ - .03, face.gray)
  const openAngle = Math.PI / 4, pressedAngle = Math.PI * 85 / 180, handleLength = .28
  const pivotPoint = new THREE.Vector3(machineX + .065 + handleLength / 2 * Math.sin(-.35), 2.35 - handleLength / 2 * Math.cos(-.35), machineZ - .02)
  const rotationAxis = new THREE.Vector3(1, 0, 0)
  const handle = new THREE.Group()
  handle.name = 'fixture-handle-pivot'
  handle.position.copy(pivotPoint)
  root.add(handle)
  rod(.022, handleLength, 0, handleLength / 2, 0, face.white, handle)
  const grip = outlined(new THREE.SphereGeometry(.065, 16, 12), face.blue, handle)
  const gripOffset = new THREE.Vector3(0, handleLength, 0)
  grip.position.copy(gripOffset)
  grip.name = 'fixture-handle-grip'
  box(.23, .45, .32, machineX - .64, 1.37, machineZ - .06)
  box(.015, .29, .20, machineX - .517, 1.39, machineZ - .06, face.gray)
  for (const y of [1.34, 1.43]) rod(.018, .023, machineX - .54, y, machineZ + .112, face.blue)

  const plate = new THREE.Group()
  plate.position.set(machineX, 1.72, machineZ)
  root.add(plate)
  box(.74, .12, .51, 0, 0, 0, normalFaces, plate)
  for (const x of [-.085, .085]) for (const z of [-.065, .065]) rod(.017, .085, x, -.1025, z, face.gray, plate)
  for (const x of [-.39, .39]) rod(.062, .17, x, .02, -.12, face.white, plate)
  const plunger = rod(.045, 1, machineX, 1.90, machineZ - .03, face.pale)

  const loading = new THREE.Group()
  root.add(loading)
  const dots = [-.20, 0, .20].map(offset => {
    const fill = material(palette.pale, { transparent: true })
    const dot = new THREE.Mesh(geometry(new THREE.CircleGeometry(.042, 24)), fill)
    dot.position.set(.86 + offset, 1.77, -.541)
    loading.add(dot)
    return fill
  })
  const tick = new THREE.Shape()
  // Parallel edges and a shared miter keep both 45-degree arms equally thick.
  const halfDiagonal = .105 / (2 * Math.SQRT2)
  tick.moveTo(-.20 + halfDiagonal, halfDiagonal)
  tick.lineTo(-.07, -.13 + 2 * halfDiagonal)
  tick.lineTo(.24 - halfDiagonal, .18 + halfDiagonal)
  tick.lineTo(.24 + halfDiagonal, .18 - halfDiagonal)
  tick.lineTo(-.07, -.13 - 2 * halfDiagonal)
  tick.lineTo(-.20 - halfDiagonal, -halfDiagonal)
  tick.closePath()
  const success = new THREE.Mesh(geometry(new THREE.ShapeGeometry(tick)), face.green)
  success.name = 'quality-success-checkmark'
  success.position.set(.86, 1.77, -.540)
  root.add(success)
  const bounds = new THREE.Mesh(geometry(new THREE.BoxGeometry(4.8, 2.62, 3.4)), face.ground)
  bounds.position.y = 1.295
  bounds.visible = false
  root.add(bounds)
  const easeOut = value => { const t = THREE.MathUtils.clamp(value, 0, 1); return 1 - (1 - t) ** 3 }
  const rebound = value => { const t = THREE.MathUtils.clamp(value, 0, 1) - 1; return 1 + 2.05 * t ** 3 + 1.05 * t ** 2 }
  function update(seconds) {
    const phase = ((seconds % cycleSeconds) + cycleSeconds) % cycleSeconds / cycleSeconds
    let press = 0, stage = 'loading'
    if (phase >= .10 && phase < .22) { press = easeOut((phase - .10) / .12); stage = 'clamping' }
    else if (phase >= .22 && phase < .46) { press = 1; stage = 'testing' }
    else if (phase >= .46 && phase < .64) { press = 1; stage = 'success' }
    else if (phase >= .64 && phase < .78) { press = 1 - rebound((phase - .64) / .14); stage = 'releasing' }
    const plateY = THREE.MathUtils.lerp(1.72, 1.39, press)
    plate.position.y = plateY
    // The lowest probe ends at 1.245, exactly the top of the chip, when fully pressed.
    const plungerBottom = plateY + .06, plungerTop = 2.04
    plunger.position.y = (plungerBottom + plungerTop) / 2
    plunger.scale.y = plungerTop - plungerBottom
    const handleAngle = THREE.MathUtils.lerp(openAngle, pressedAngle, press)
    // Positive X rotation brings the handle toward the front (+Z) of the table.
    handle.rotation.x = handleAngle
    const gripPosition = gripOffset.clone().applyAxisAngle(rotationAxis, handleAngle).add(pivotPoint)
    const successful = phase >= .46 && phase < .78
    loading.visible = !successful
    success.visible = successful
    const dotOpacities = dots.map((fill, index) => {
      const value = .20 + .80 * (.5 + .5 * Math.cos(phase * Math.PI * 8 - index * Math.PI * 2 / 3))
      fill.opacity = value
      return value
    })
    Object.assign(root.userData, {
      phase, cycleSeconds, stage, press, plateY, probeBottom: plateY - .145, chipTop: 1.245,
      handleAngle, handlePivot: pivotPoint.toArray(), gripPosition: gripPosition.toArray(), gripRadius: gripOffset.length(), handleLength,
      loading: !successful, success: successful, dotOpacities,
    })
    return root.userData
  }
  // Fit the complete swept envelope using actual vertices at the shared isometric angle.
  const viewDirection = new THREE.Vector3(1, Math.sqrt(2 / 3), 1).normalize()
  const screenRight = new THREE.Vector3(1, 0, -1).normalize()
  const screenUp = new THREE.Vector3().crossVectors(viewDirection, screenRight).normalize()
  const directions = [screenRight, screenRight.clone().negate(), screenUp, screenUp.clone().negate()]
  const extrema = directions.map(() => ({ distance: -Infinity, point: new THREE.Vector3() }))
  const vertex = new THREE.Vector3()
  for (let step = 0; step <= 96; step++) {
    update(step * cycleSeconds / 96)
    root.updateMatrixWorld(true)
    root.traverseVisible(object => {
      const positions = object.geometry?.attributes.position
      if (!positions) return
      for (let index = 0; index < positions.count; index++) {
        vertex.fromBufferAttribute(positions, index).applyMatrix4(object.matrixWorld)
        directions.forEach((direction, axis) => {
          const distance = vertex.dot(direction)
          if (distance > extrema[axis].distance) { extrema[axis].distance = distance; extrema[axis].point.copy(vertex) }
        })
      }
    })
  }
  const framingPoints = extrema.map(value => value.point)
  const framingBounds = new THREE.Box3(new THREE.Vector3(-2.4, -.015, -1.7), new THREE.Vector3(2.4, 2.605, 1.7))
  update(staticTime)
  return { root, update, cycleSeconds, staticTime, framingPoints, framingBounds, getState: () => root.userData, dispose() {
    geometries.forEach(value => value.dispose())
    materials.forEach(value => value.dispose())
  } }
}
